import { defineStore } from 'pinia';
import axios from 'axios';
import { useAuthStore } from './auth';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL ?? 'http://localhost:1337';
const INVALID_FILENAME_CHARS = /[<>:"/\\|?*\u0000-\u001F]/g;
const WINDOWS_RESERVED_NAME = /^(con|prn|aux|nul|com\d|lpt\d)$/i;
const MAX_FILENAME_LENGTH = 255;

export const sanitizeUploadFilename = (filename = '') => {
  const rawName = String(filename || '').normalize().trim();
  if (!rawName) {
    return 'file';
  }

  const lastDotIndex = rawName.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0 && lastDotIndex < rawName.length - 1;
  const baseName = hasExtension ? rawName.slice(0, lastDotIndex) : rawName;
  const extension = hasExtension ? rawName.slice(lastDotIndex) : '';

  const safeBaseName = baseName
    .replace(INVALID_FILENAME_CHARS, '_')
    .replace(/_+/g, '_')
    .trim()
    .replace(/\.+$/g, '') || 'file';
  const normalizedBaseName =
    safeBaseName === '.' ||
    safeBaseName === '..' ||
    WINDOWS_RESERVED_NAME.test(safeBaseName)
      ? `file_${safeBaseName.replace(/\.+$/g, '') || 'upload'}`
      : safeBaseName;
  const safeExtension = extension.replace(INVALID_FILENAME_CHARS, '').trim();
  const maxBaseLength = Math.max(1, MAX_FILENAME_LENGTH - safeExtension.length);
  const truncatedBaseName = normalizedBaseName.slice(0, maxBaseLength).trim() || 'file';

  return `${truncatedBaseName}${safeExtension}`.slice(0, MAX_FILENAME_LENGTH);
};

export const useMediaStore = defineStore('media', {
  actions: {
    authHeaders() {
      const authStore = useAuthStore();
      return authStore.token ? authStore.requestHeaders.headers : {};
    },

    async uploadFile(file) {
      if (!file) {
        throw new Error('File is required for upload');
      }
      const headers = this.authHeaders();
      const formData = new FormData();
      const uploadFilename = sanitizeUploadFilename(file?.name || 'file');
      formData.append('files', file, uploadFilename);

      let response;
      try {
        response = await axios.post(`${STRAPI_URL}/api/upload`, formData, {
          headers,
        });
      } catch (error) {
        const responseMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'Upload failed';
        if (error instanceof Error) {
          error.message = responseMessage;
          throw error;
        }
        throw new Error(responseMessage);
      }

      const data = response?.data;
      const uploaded = Array.isArray(data) ? data[0] : data;
      if (!uploaded?.id) {
        throw new Error('Upload failed: missing file id');
      }
      return uploaded;
    },

    async deleteFile(id) {
      if (!id) return;
      const headers = this.authHeaders();
      await axios.delete(`${STRAPI_URL}/api/upload/files/${id}`, { headers });
    },

    async getSignedUrl(id, options = {}) {
      if (!id) {
        throw new Error('File id is required to request a signed URL');
      }
      const headers = this.authHeaders();
      const disposition = ['view', 'download'].includes(options?.disposition)
        ? options.disposition
        : null;
      const response = await axios.get(`${STRAPI_URL}/api/signed-media/${id}`, {
        headers,
        ...(disposition ? { params: { disposition } } : {}),
      });
      return response?.data;
    },
  },
});
